import React, { useState, useEffect } from 'react';
import { Popover, Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { notificationService, Notification } from '../../services/notificationService';
import { tokenService } from '../../services/tokenService';
import { MaterialSymbol } from '../common/MaterialSymbol';

/**
 * Icon-Zuordnung: notificationService liefert bereits Material-Symbols-Namen
 * (school, emoji_events, rate_review, …). Kunden-Feedback: Zertifikate nutzen
 * das Pokal-Icon statt card_membership.
 */
const resolveIcon = (iconName: string | null | undefined, type: string): string => {
    const name = iconName || notificationService.getIconForType(type);
    if (name === 'card_membership') return 'emoji_events';
    if (name === 'person_remove') return 'person';
    return name || 'notifications';
};

/**
 * Benachrichtigungs-Glocke + Dropdown im Design-System-Stil
 * ("Benachrichtigungs-Menü"): Glocke als .iconbtn mit Zähler-Badge,
 * Einträge mit neutral-grauem Icon-Kreis, Ungelesen-Punkt, "Alle gelesen".
 */
const NotificationCenter: React.FC = () => {
    const navigate = useNavigate();
    const translate = useTranslate();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const open = Boolean(anchorEl);

    // Load unread count on mount and periodically.
    // Token-Guard wie in CartContext: ohne gültiges Token würde der Call
    // mit 401 scheitern und im Audit als "Failed to fetch" auflaufen,
    // während die Login-Seite noch rendert.
    useEffect(() => {
        if (!tokenService.hasValidToken()) return;
        loadUnreadCount();
        const interval = setInterval(loadUnreadCount, 30000); // Every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const loadUnreadCount = async () => {
        try {
            const count = await notificationService.getUnreadCount();
            setUnreadCount(count);
        } catch (err) {
            console.error('Failed to load unread count:', err);
        }
    };

    const loadNotifications = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await notificationService.getNotifications(false, 20);
            setNotifications(data.notifications);
            setUnreadCount(data.unreadCount);
        } catch (err) {
            setError('Failed to load notifications');
            console.error('Failed to load notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        loadNotifications();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read
        if (!notification.isRead) {
            try {
                await notificationService.markAsRead(notification.id);
                setNotifications(prev =>
                    prev.map(n =>
                        n.id === notification.id ? { ...n, isRead: true } : n
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (err) {
                console.error('Failed to mark notification as read:', err);
            }
        }

        // Navigate if action URL exists
        if (notification.actionUrl) {
            handleClose();
            if (/^https?:\/\//i.test(notification.actionUrl)) {
                window.location.assign(notification.actionUrl);
            } else {
                navigate(notification.actionUrl);
            }
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return translate('notifications.justNow', { _: 'Gerade eben' });
        if (diffMins < 60) return translate('notifications.minutesAgo', { count: diffMins, _: `Vor ${diffMins} Min.` });
        if (diffHours < 24) return translate('notifications.hoursAgo', { count: diffHours, _: `Vor ${diffHours} Std.` });
        if (diffDays < 7) return translate('notifications.daysAgo', { count: diffDays, _: `Vor ${diffDays} Tagen` });

        return date.toLocaleDateString();
    };

    return (
        <>
            <button
                className="iconbtn"
                onClick={handleClick}
                aria-label={translate('notifications.title', { _: 'Benachrichtigungen' })}
            >
                <MaterialSymbol name="notifications" size={22} />
                {unreadCount > 0 && <span className="dot">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        width: 384,
                        maxWidth: '92vw',
                        borderRadius: 'var(--oa-radius-md)',
                        border: '1px solid var(--oa-divider)',
                        boxShadow: '0 14px 44px rgba(0,0,0,.2)',
                        bgcolor: 'var(--oa-paper)',
                        overflow: 'hidden',
                    },
                }}
            >
                <div className="notif-head">
                    <span className="t">
                        {translate('notifications.title', { _: 'Benachrichtigungen' })}
                        {unreadCount > 0 && <span className="c">{unreadCount}</span>}
                    </span>
                    {unreadCount > 0 && (
                        <button className="read" onClick={handleMarkAllRead}>
                            <MaterialSymbol name="done_all" size={16} />
                            {translate('notifications.markAllRead', { _: 'Alle gelesen' })}
                        </button>
                    )}
                </div>

                <div className="notif-list">
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}

                    {error && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    )}

                    {!loading && !error && notifications.length === 0 && (
                        <div className="notif-empty">
                            <MaterialSymbol name="notifications" size={38} style={{ opacity: 0.45, marginBottom: 8 }} />
                            <p style={{ margin: 0 }}>
                                {translate('notifications.noNotifications', { _: 'Keine Benachrichtigungen' })}
                            </p>
                        </div>
                    )}

                    {!loading && !error && notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={`notif-item${notification.isRead ? '' : ' unread'}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <span className="nico info">
                                <MaterialSymbol name={resolveIcon(notification.icon, notification.type)} size={20} />
                            </span>
                            <div className="nbody">
                                <p className="nt">{notification.title}</p>
                                {notification.message && <p className="nx">{notification.message}</p>}
                                <p className="nd">{formatDate(notification.createdAt)}</p>
                            </div>
                            <span className="udot"></span>
                        </div>
                    ))}
                </div>
            </Popover>
        </>
    );
};

export default NotificationCenter;
